<script>
const SLIDER_MAX = 300;

function lerp(a, b, t) { return a + (b - a) * t; }

export default {
  name: 'ModalEditCropStats',

  props: {
    cut:      { type: Object, default: null },
    videoUrl: { type: String, default: null },
  },

  data() {
    return {
      showModal: false,
      SLIDER_MAX,
      xOffset:            0,
      keyframes:          [],   // [{ time, x_offset }] — time relative to cut start
      isPlaying:          false,
      isDragging:         false,
      currentTime:        0,
      playbackSpeed:      0.5,
      playCheckInterval:  null,
      // Rendered video content area (excludes letterbox bars)
      videoNaturalW:      0,
      videoNaturalH:      0,
      containerW:         0,
      containerH:         0,
    };
  },

  computed: {
    cutStart() {
      if (!this.cut) return 0;
      return this.cut.clip_start_sec != null ? this.cut.clip_start_sec : (this.cut.start || 0);
    },
    cutEnd() {
      if (!this.cut) return 0;
      return this.cut.clip_end_sec != null ? this.cut.clip_end_sec : (this.cut.end || 0);
    },
    cutDuration() {
      return Math.max(this.cutEnd - this.cutStart, 0.001);
    },
    relativeTime() {
      return Math.max(0, this.currentTime - this.cutStart);
    },
    activeXOffset() {
      if (this.isPlaying && this.keyframes.length > 0) {
        return this.interpolateAt(this.relativeTime);
      }
      return this.xOffset;
    },

    cropBoxWidthPct() { return (81 / 256) * 100; },

    offsetLabel() {
      const v = Math.round(this.activeXOffset);
      if (v === 0) return 'Center';
      return v > 0 ? `+${v}px right` : `${v}px left`;
    },

    sortedKeyframes() {
      return [...this.keyframes].sort((a, b) => a.time - b.time);
    },

    // The rendered video content rectangle inside the letterboxed element
    videoContentArea() {
      const vW = this.videoNaturalW, vH = this.videoNaturalH;
      const cW = this.containerW,   cH = this.containerH;
      if (!vW || !vH || !cW || !cH) return null;
      const scale = Math.min(cW / vW, cH / vH);
      const w = vW * scale, h = vH * scale;
      return {
        position: 'absolute',
        left:   Math.round((cW - w) / 2) + 'px',
        top:    Math.round((cH - h) / 2) + 'px',
        width:  Math.round(w) + 'px',
        height: Math.round(h) + 'px',
      };
    },
  },

  watch: {
    cut(val) {
      if (val) this.$nextTick(() => this.seekToStart());
    },
  },

  beforeDestroy() {
    this.stopPreview();
    this._detachDrag();
    this._cancelVisual();
  },

  methods: {
    open() {
      // Always start from the last SAVED state — discards any unsaved edits left over
      // from a previous Cancel/close, before the modal becomes visible.
      this._initFromCut();
      this.showModal = true;
    },
    close() { this.stopPreview(); this.showModal = false; },

    onModalShown() {
      this._scrubbing    = false;
      this._offsetActive = false;
      this._initFromCut();
      this.seekToStart();
      this.$nextTick(() => {
        this.updateContainerSize();
        this._attachDrag();
        this.applyVisualLeft(this.xOffset);
        this._syncSliderThumb(this.xOffset);
        this._syncScrubThumb(this.currentTime);
      });
      this._resizeHandler = () => this.updateContainerSize();
      window.addEventListener('resize', this._resizeHandler);
      this._keyHandler = (e) => this.onArrowKey(e);
      window.addEventListener('keydown', this._keyHandler);
    },

    _initFromCut() {
      const cs = this.cut && this.cut.crop_stats;
      if (cs && Array.isArray(cs.keyframes) && cs.keyframes.length) {
        this.keyframes = cs.keyframes.map(k => ({ ...k }));
        this.xOffset   = this.interpolateAt(0);
      } else if (cs && cs.x_offset !== undefined) {
        this.keyframes = [];
        this.xOffset   = cs.x_offset;
      } else {
        this.keyframes = [];
        this.xOffset   = 0;
      }
    },

    onModalHidden() {
      this.stopPreview();
      this._detachDrag();
      this._cancelVisual();
      clearTimeout(this._kfDebounce);
      window.removeEventListener('resize', this._resizeHandler);
      window.removeEventListener('keydown', this._keyHandler);
      this.$emit('hidden');
    },

    _attachDrag() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this._dragBase  = null;
      this._onPtrDown = (e) => {
        e.preventDefault();
        this._dragBase     = { x: e.clientX, offset: this.xOffset };
        this._liveOffset   = this.xOffset;
        this._offsetActive = true;   // suppress playback interpolation while the user drives the offset
        v.setPointerCapture(e.pointerId);
        this.isDragging = true;
      };
      this._onPtrMove = (e) => {
        if (!this._dragBase) return;
        const dx  = e.clientX - this._dragBase.x;
        const val = Math.min(Math.max(this._dragBase.offset + Math.round(dx), -SLIDER_MAX), SLIDER_MAX);
        this._liveOffset = val;
        this._scheduleVisual(val);
      };
      this._onPtrUp = () => {
        if (!this._dragBase) return;
        this._dragBase     = null;
        this.isDragging    = false;
        this._offsetActive = false;
        this.xOffset       = this._liveOffset;
        this.addKeyframe();
      };
      v.addEventListener('pointerdown',   this._onPtrDown);
      v.addEventListener('pointermove',   this._onPtrMove);
      v.addEventListener('pointerup',     this._onPtrUp);
      v.addEventListener('pointercancel', this._onPtrUp);
    },

    _detachDrag() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      v.removeEventListener('pointerdown',   this._onPtrDown);
      v.removeEventListener('pointermove',   this._onPtrMove);
      v.removeEventListener('pointerup',     this._onPtrUp);
      v.removeEventListener('pointercancel', this._onPtrUp);
    },

    onVideoLoadedMetadata() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.videoNaturalW = v.videoWidth;
      this.videoNaturalH = v.videoHeight;
      this.$nextTick(() => this.updateContainerSize());
    },

    updateContainerSize() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.containerW = v.clientWidth;
      this.containerH = v.clientHeight;
    },

    // ── Video ─────────────────────────────────────────────────

    seekToStart() {
      const v = this.$refs.previewVideo;
      if (v && this.cut) { v.currentTime = this.cutStart; this.currentTime = this.cutStart; this._syncScrubThumb(this.cutStart); }
    },

    seekTo(t) {
      const v = this.$refs.previewVideo;
      if (v) { v.currentTime = t; this.currentTime = t; this._syncScrubThumb(t); }
    },

    // Push playback position into the (uncontrolled) scrub thumb
    _syncScrubThumb(t) {
      const s = this.$refs.scrubSlider;
      if (s) s.value = String(t);
    },

    // User scrubbing: seek the video, but never let timeupdate write the thumb back mid-drag
    onScrubInput(e) {
      this._scrubbing = true;
      const t = +e.target.value;
      const v = this.$refs.previewVideo;
      if (v) v.currentTime = t;
      this.currentTime = t;   // drives the time label only — scrub bar is uncontrolled now
    },

    onScrubChange() {
      this._scrubbing = false;
    },

    // Write position directly to element styles — bypasses Vue reactivity entirely
    applyVisualLeft(xOffset) {
      const half = (100 - this.cropBoxWidthPct) / 2;
      const clamped = Math.min(Math.max(xOffset, -SLIDER_MAX), SLIDER_MAX);
      const leftPct = half + (clamped / SLIDER_MAX) * half;
      const box = this.$refs.cropBox;
      if (box) box.style.left = leftPct + '%';
    },

    // Push a value into the (uncontrolled) slider thumb. Only used for programmatic
    // changes — never during the user's own slider drag, to avoid fighting the browser.
    _syncSliderThumb(val) {
      const s = this.$refs.offsetSlider;
      if (s) s.value = String(Math.min(Math.max(val, -SLIDER_MAX), SLIDER_MAX));
    },

    // Coalesce gesture updates into one frame — direct DOM only, no reactive writes
    _scheduleVisual(val, syncThumb = true) {
      this._pendingOffset    = val;
      this._pendingSyncThumb = syncThumb;
      if (this._rafId != null) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        const v = this._pendingOffset;
        this.applyVisualLeft(v);
        if (this._pendingSyncThumb) this._syncSliderThumb(v);
        const lbl = this.$refs.offsetLabelText;
        if (lbl) lbl.textContent = this._offsetText(v);
      });
    },

    _offsetText(val) {
      const v = Math.round(val);
      if (v === 0) return 'Center';
      return v > 0 ? `+${v}px right` : `${v}px left`;
    },

    _cancelVisual() {
      if (this._rafId != null) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    },

    onSliderInput(e) {
      const val = +e.target.value;
      this._offsetActive = true;         // suppress playback interpolation while the user drives the offset
      this._liveOffset = val;
      this._scheduleVisual(val, false);  // never write the slider value back to itself mid-drag
    },

    onSliderChange() {
      this._offsetActive = false;
      this.xOffset = this._liveOffset;
      // Commit the keyframe immediately (not debounced) so resuming playback interpolation
      // sees the new value at this time and doesn't snap the box back to the old keyframes.
      clearTimeout(this._kfDebounce);
      this.addKeyframe();
    },

    onArrowKey(e) {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      const step = e.shiftKey ? 30 : 5;
      if (e.key === 'ArrowLeft')  this.xOffset = Math.max(this.xOffset - step, -SLIDER_MAX);
      if (e.key === 'ArrowRight') this.xOffset = Math.min(this.xOffset + step, SLIDER_MAX);
      if (e.key === 'ArrowDown')  this.xOffset = 0;
      this.applyVisualLeft(this.xOffset);
      this._syncSliderThumb(this.xOffset);
      // Commit immediately (not debounced) so resuming playback interpolation sees the
      // new value at this time and doesn't snap the box back to the old keyframes.
      clearTimeout(this._kfDebounce);
      this.addKeyframe();
    },

    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed;
      const v = this.$refs.previewVideo;
      if (v) v.playbackRate = speed;
    },

    playPreview() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this._scrubbing = false;   // ensure the scrub thumb follows playback
      v.currentTime  = this.cutStart;
      v.playbackRate = this.playbackSpeed;
      v.play();
      this.playCheckInterval = setInterval(() => {
        if (!this.$refs.previewVideo) return;
        this.currentTime = this.$refs.previewVideo.currentTime;
        if (this.$refs.previewVideo.currentTime >= this.cutEnd) this.stopPreview();
      }, 80);
    },

    stopPreview() {
      clearInterval(this.playCheckInterval);
      this.playCheckInterval = null;
      const v = this.$refs.previewVideo;
      if (v) v.pause();
    },

    onVideoPlay()  { this.isPlaying = true; },
    onVideoPause() { this.isPlaying = false; this._restoreToOffset(); },
    onVideoEnded() { this.isPlaying = false; clearInterval(this.playCheckInterval); this._restoreToOffset(); },

    // After playback, brackets/thumb were tracking interpolated keyframes — snap back to the edit offset.
    // Skip while the user is actively adjusting (e.g. an auto-pause triggered by grabbing the slider),
    // otherwise we'd yank the box back to the pre-drag offset mid-gesture.
    _restoreToOffset() {
      if (this._offsetActive) return;
      this.applyVisualLeft(this.xOffset);
      this._syncSliderThumb(this.xOffset);
    },
    onVideoTimeUpdate() {
      const v = this.$refs.previewVideo;
      if (!v) return;
      this.currentTime = v.currentTime;
      // Move the scrub thumb to follow playback, but never while the user is dragging it
      if (!this._scrubbing) this._syncScrubThumb(v.currentTime);
      // Animate the crop offset along keyframes during playback — but not while the
      // user is actively setting the offset (that would fight their drag).
      if (this.isPlaying && this.keyframes.length > 0 && !this._offsetActive) {
        const relT = Math.max(0, v.currentTime - this.cutStart);
        const interp = this.interpolateAt(relT);
        this.applyVisualLeft(interp);
        this._syncSliderThumb(interp);
      }
    },


    // ── Keyframes ─────────────────────────────────────────────

    addKeyframe() {
      const relT     = Math.round(this.relativeTime * 100) / 100;
      const existing = this.keyframes.findIndex(k => Math.abs(k.time - relT) < 0.05);
      if (existing !== -1) {
        this.$set(this.keyframes[existing], 'x_offset', this.xOffset);
      } else {
        this.keyframes.push({ time: relT, x_offset: this.xOffset });
        this.keyframes.sort((a, b) => a.time - b.time);
      }
    },

    interpolateAt(relT) {
      const kfs = this.sortedKeyframes;
      if (!kfs.length)      return this.xOffset;
      if (kfs.length === 1) return kfs[0].x_offset;
      if (relT <= kfs[0].time) return kfs[0].x_offset;
      if (relT >= kfs[kfs.length - 1].time) return kfs[kfs.length - 1].x_offset;
      for (let i = 0; i < kfs.length - 1; i++) {
        if (relT >= kfs[i].time && relT <= kfs[i + 1].time) {
          const t = (relT - kfs[i].time) / (kfs[i + 1].time - kfs[i].time);
          return lerp(kfs[i].x_offset, kfs[i + 1].x_offset, t);
        }
      }
      return this.xOffset;
    },

    // ── Save / Export ──────────────────────────────────────────

    buildCropStats() {
      const sorted = [...this.keyframes].sort((a, b) => a.time - b.time);
      if (sorted.length) return { keyframes: sorted };
      return { x_offset: this.xOffset };
    },

    exportJson() {
      if (!this.cut) return;
      const payload = {
        cut_id:     this.cut.id,
        title:      this.cut.title || this.cut.label || '',
        event_type: this.cut.event_type || this.cut.eventType || '',
        start:      this.cutStart,
        end:        this.cutEnd,
        duration:   Math.round((this.cutEnd - this.cutStart) * 1000) / 1000,
        crop_stats: this.buildCropStats(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const a    = document.createElement('a');
      a.href     = URL.createObjectURL(blob);
      a.download = `cut-${this.cut.id}-crop.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    },

    save() {
      if (!this.cut) return;
      const cropStats = this.buildCropStats();
      this.$emit('saved', { cutId: this.cut.id, crop_stats: cropStats });
      this.close();
    },

    formatSecs(secs) {
      if (secs == null || isNaN(secs)) return '0:00';
      const s = Math.floor(Number(secs));
      return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    },

    setOffset(val) {
      this.xOffset = val;
      this.applyVisualLeft(val);
      this._syncSliderThumb(val);
      this.addKeyframe();
    },

    formatSecsDecimal(secs) {
      if (secs == null || isNaN(secs)) return '0:00.0';
      const n = Number(secs);
      const m = Math.floor(n / 60);
      const s = (n % 60).toFixed(1).padStart(4, '0');
      return `${m}:${s}`;
    },
  },
};
</script>

<template>
  <b-modal
    id="modal-edit-crop-stats"
    v-model="showModal"
    size="xl"
    dialog-class="cev-crop-dialog"
    title="Edit Crop Position"
    title-class="font-18"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <template v-if="cut">

      <!-- ── Video with crop overlay ── -->
      <div class="cev-video-wrap mb-2" :class="{ 'cev-playing': isPlaying }">
        <video
          v-if="videoUrl"
          ref="previewVideo"
          class="cev-video"
          :src="videoUrl"
          preload="auto"
          @play="onVideoPlay"
          @pause="onVideoPause"
          @ended="onVideoEnded"
          @timeupdate="onVideoTimeUpdate"
          @loadedmetadata="onVideoLoadedMetadata"
        ></video>
        <div v-else class="cev-video-placeholder">
          <i class="mdi mdi-video-off-outline" style="font-size:36px;"></i>
          <p class="mt-1 mb-0 small">No video loaded</p>
        </div>

        <!-- Crop brackets, confined to the actual video content area (no letterbox bars).
             v-show keeps elements in DOM at all times so refs are always valid. -->
        <div v-show="!!videoContentArea" :style="videoContentArea || {}">
          <div class="cev-crop-box" ref="cropBox"></div>
          <div class="cev-center-line"></div>
        </div>
      </div>

      <!-- ── Scrub bar ── -->
      <div class="mb-2">
        <input
          ref="scrubSlider"
          type="range"
          class="form-range"
          :min="cutStart"
          :max="cutEnd"
          step="0.01"
          @input="onScrubInput"
          @change="onScrubChange"
        />
        <div class="d-flex justify-content-between" style="font-size:10px; color:#6c757d; font-family:monospace; margin-top:-4px;">
          <span>{{ formatSecs(cutStart) }}</span>
          <span>{{ formatSecs((cutStart + cutEnd) / 2) }}</span>
          <span>{{ formatSecs(cutEnd) }}</span>
        </div>
      </div>

      <!-- ── Controls row ── -->
      <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button
          class="btn btn-sm"
          :class="isPlaying ? 'btn-warning' : 'btn-outline-primary'"
          :disabled="!videoUrl"
          @click="isPlaying ? stopPreview() : playPreview()"
        >
          <i :class="isPlaying ? 'mdi mdi-stop' : 'mdi mdi-play'"></i>
          {{ isPlaying ? 'Stop' : 'Play Cut' }}
        </button>

        <!-- Speed selector -->
        <div class="btn-group btn-group-sm" role="group">
          <button
            v-for="s in [0.25, 0.5, 1]"
            :key="s"
            class="btn"
            :class="playbackSpeed === s ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setPlaybackSpeed(s)"
          >{{ s === 1 ? '1×' : s + '×' }}</button>
        </div>

        <span class="small text-muted font-monospace">
          {{ formatSecsDecimal(relativeTime) }} / {{ formatSecs(cutDuration) }}
        </span>
        <span class="ms-auto">
          <span class="badge" :class="keyframes.length ? 'bg-primary' : 'bg-secondary'">
            {{ keyframes.length }} keyframe{{ keyframes.length !== 1 ? 's' : '' }}
          </span>
        </span>
      </div>

      <!-- ── Slider ── -->
      <label class="form-label fw-semibold mb-1 d-flex justify-content-between">
        <span>Horizontal Offset</span>
        <span ref="offsetLabelText" class="text-primary font-monospace">{{ offsetLabel }}</span>
      </label>
      <div class="d-flex align-items-center gap-2 mb-2">
        <span class="small text-muted crop-slider-edge text-end">← Left</span>
        <input
          ref="offsetSlider"
          type="range"
          class="form-range flex-grow-1 crop-slider"
          :min="-SLIDER_MAX"
          :max="SLIDER_MAX"
          step="1"
          @input="onSliderInput"
          @change="onSliderChange"
        />
        <span class="small text-muted crop-slider-edge">Right →</span>
      </div>

      <!-- Position presets — each snaps the crop box and adds a keyframe at current time -->
      <div class="d-flex gap-2 justify-content-center flex-wrap mt-4">
        <button class="btn btn-sm btn-outline-secondary cev-preset-btn" @click="setOffset(-SLIDER_MAX)">
          <i class="mdi mdi-arrow-collapse-left"></i>
          <span>Left</span>
          <kbd class="cev-kbd">←</kbd>
        </button>
        <button class="btn btn-sm btn-outline-primary cev-preset-btn" @click="setOffset(0)">
          <i class="mdi mdi-format-horizontal-align-center"></i>
          <span>Center</span>
          <kbd class="cev-kbd">↓</kbd>
        </button>
        <button class="btn btn-sm btn-outline-secondary cev-preset-btn" @click="setOffset(SLIDER_MAX)">
          <i class="mdi mdi-arrow-collapse-right"></i>
          <span>Right</span>
          <kbd class="cev-kbd">→</kbd>
        </button>
      </div>
      <p class="text-center text-muted mt-2 mb-0" style="font-size:11px;">
        Use <kbd class="cev-kbd-inline">←</kbd> <kbd class="cev-kbd-inline">→</kbd> to nudge &nbsp;·&nbsp;
        <kbd class="cev-kbd-inline">↓</kbd> to center &nbsp;·&nbsp;
        <kbd class="cev-kbd-inline">Shift</kbd> + arrow for bigger steps
      </p>

    </template>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="close">Cancel</b-button>
      <b-button variant="primary" @click="save">
        Save Crop
      </b-button>
    </template>
  </b-modal>
</template>

<!-- Non-scoped: the modal dialog renders at <body>, outside this component's scope -->
<style>
.cev-crop-dialog { max-width: 1350px; }
</style>

<style scoped>
/* ── Video wrap ──────────────────────────────────────────── */
.cev-video-wrap {
  position: relative;
  width: fit-content;      /* hug the video so there are no letterbox bars */
  max-width: 100%;
  margin: 0 auto;          /* center within the modal body */
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  line-height: 0;
}
.cev-video {
  display: block;
  width: auto;             /* size by aspect ratio from the height, not forced full width */
  height: auto;
  max-width: 100%;
  max-height: 620px;
  object-fit: contain;
  background: #000;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
  touch-action: none;
}
.cev-video:active { cursor: grabbing; }
.cev-video-placeholder {
  width: 640px;
  max-width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  background: #1a1a2e;
  line-height: 1.4;
}

/* Crop box — left set directly via JS refs */
.cev-crop-box {
  position: absolute;
  top: 0; bottom: 0;
  width: 31.640625%;
  border-left:  2px solid #f0c040;
  border-right: 2px solid #f0c040;
  pointer-events: none;
  will-change: left;
}

/* Smooth interpolation only during playback (not during drag/slider interaction) */
.cev-playing .cev-crop-box { transition: left 0.08s linear; }
.cev-center-line {
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 1px;
  background: rgba(255,255,255,0.2);
  pointer-events: none;
}


/* ── Slider ──────────────────────────────────────────────── */
.crop-slider {
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 24px;            /* tall enough to fit the thumb; visible track drawn via pseudo-elements */
  background: transparent;
  outline: none;
}
/* Track */
.crop-slider::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 4px;
  background: #d6dbe1;
}
.crop-slider::-moz-range-track {
  height: 8px;
  border-radius: 4px;
  background: #d6dbe1;
}
/* Bigger circular drag handle */
.crop-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0d6efd;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  cursor: grab;
  margin-top: -8px;        /* center on the 8px track: (8 - 24) / 2 */
}
.crop-slider::-webkit-slider-thumb:active { cursor: grabbing; }
.crop-slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0d6efd;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  cursor: grab;
}
.crop-slider-edge { min-width: 52px; font-size: 11px; }

/* ── Position preset buttons ─────────────────────────────── */
.cev-preset-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  min-width: 80px;
  padding: 2px 12px;
  font-size: 13px;
  line-height: 1.25;
}

.cev-kbd {
  display: block;
  margin-top: 1px;
  font-size: 11px;
  background: #e9ecef;
  border: 1px solid #adb5bd;
  border-radius: 3px;
  padding: 0 5px;
  color: #343a40;
  line-height: 1.3;
}
.cev-kbd-inline {
  font-size: 10px;
  background: #e9ecef;
  border: 1px solid #adb5bd;
  border-radius: 3px;
  padding: 0 4px;
  color: #343a40;
  line-height: 1.6;
}
</style>
